import { useState, useContext, useEffect } from 'react';
import {
    Button,
    Col,
    Row,
    Form,
    FormGroup,
    Input,
    Label,
    Spinner,
} from 'reactstrap';
import { useHistory } from 'react-router-dom';
import { CampaignContext, CampaignState } from '~/context/campaign';
import { SelectOption } from '~/types/globals';
import { useForm } from 'react-hook-form';
import {
    CheckboxInput,
    PhoneInput,
    SelectInput,
    TextInput,
} from '~/shared/components/form/Inputs';
import { fetchMedicalProfessionalTypes } from '~/services/options';
import { addNewMedicalProfessional } from '~/services/registration';
import { identityOptions } from '~/shared/config';
import moment from 'moment';
import { AdminContext } from '~/context/admin';

export const MedCenterForm = ({
    location,
    selectedMedicalCenter = null,
    onCancel,
    profOnly = false,
}: any) => {
    const [{ campaign }, campaignDispatch]: [CampaignState, any] =
        useContext(CampaignContext);
    const [{ isAdmin, campaignId }, adminDispatch] = useContext(AdminContext);
    const [medicalProfessionalTypeOptions, setMedicalProfessionalTypeOptions] =
        useState<SelectOption[]>([]);
    const today = moment().format('Y-MM-DD');
    const history = useHistory();

    const {
        control,
        handleSubmit,
        setValue,
        formState: { errors, isSubmitting, isValid },
    } = useForm({
        defaultValues: {
            med_center_name: '',
            med_prof_first_name: '',
            med_prof_middle_name: '',
            med_prof_last_name: '',
            med_prof_type: '',
            med_prof_email: '',
            med_prof_phone: '',
            mp_give_consent_for_mp_to_provide_info: false,
            mp_certify_identity: '',
            mp_signature: '',
            mp_signature_date: today,
        },
    });

    const getMedicalProfessionalTypes = async () => {
        const resp = await fetchMedicalProfessionalTypes();
        setMedicalProfessionalTypeOptions(
            resp.map((item: any) => {
                return {
                    value: item.id,
                    label: item.name,
                };
            })
        );
    };

    useEffect(() => {
        if (medicalProfessionalTypeOptions.length === 0) {
            getMedicalProfessionalTypes();
        }
    }, []);

    const onSubmit = async (data: any) => {
        console.log(data);

        const {
            mp_give_consent_for_mp_to_provide_info,
            mp_certify_identity,
            mp_signature,
        } = data;

        const person = {
            first_name: data.med_prof_first_name,
            middle_name: data.med_prof_middle_name,
            last_name: data.med_prof_last_name,
            email: data.med_prof_email,
            phone: data.med_prof_phone,
        };

        try {
            const resp = await addNewMedicalProfessional(
                profOnly ? null : data.med_center_name,
                selectedMedicalCenter ? selectedMedicalCenter.value : null,
                location,
                person,
                mp_give_consent_for_mp_to_provide_info,
                mp_certify_identity,
                mp_signature,
                campaignId
            );
            campaignDispatch({
                type: 'set_campaign',
                payload: resp,
            });
            history.push('/dashboard');
        } catch (e) {
            console.log('addMedicalProfessional e: ', e);
        }
    };

    return (
        <Form>
            {!profOnly && (
                <Row>
                    <TextInput
                        name='med_center_name'
                        label='Enter the Medical Center Name'
                        control={control}
                        required={true}
                        ariaRequired={true}
                    />
                </Row>
            )}
            <Row>
                <Col>
                    <TextInput
                        name='med_prof_first_name'
                        label='Medical Professional First Name'
                        control={control}
                        required={true}
                        ariaRequired={true}
                    />
                    <TextInput
                        name='med_prof_middle_name'
                        label='Medical Professional Middle Name'
                        control={control}
                        required={false}
                    />
                    <TextInput
                        name='med_prof_last_name'
                        label='Medical Professional Last Name'
                        control={control}
                        required={true}
                        ariaRequired={true}
                    />
                    <SelectInput
                        name='med_prof_type'
                        label='Medical Professional Type'
                        control={control}
                        options={medicalProfessionalTypeOptions}
                        required={true}
                        ariaRequired={true}
                    />
                    <TextInput
                        name='med_prof_email'
                        type='email'
                        label='Medical Professional Email'
                        control={control}
                        required={false}
                    />
                    <PhoneInput
                        name='med_prof_phone'
                        label='Medical Professional Phone'
                        control={control}
                        required={false}
                    />
                </Col>
            </Row>
            <SelectInput
                name='mp_certify_identity'
                label='I certify that I am the:'
                control={control}
                options={identityOptions}
                required={true}
                ariaRequired={true}
            />

            <TextInput
                name='mp_signature'
                label='Name of individual saving these agreements'
                control={control}
                required={true}
                ariaRequired={true}
            />

            <FormGroup>
                <Label>Date</Label>
                <Input
                    type='date'
                    name='mp_signature_date'
                    id='mp_signature_date'
                    onChange={(e) => {
                        setValue('mp_signature_date', e.target.value);
                    }}
                    defaultValue={today}
                />
            </FormGroup>

            <CheckboxInput
                name='mp_give_consent_for_mp_to_provide_info'
                label='I give consent to their health care professional to provide information to Help Hope Live confirming their medical and financial need for the purpose of fundraising.'
                control={control}
                required={true}
                ariaRequired={true}
            />
            <Row className='mb-5'>
                <Col>
                    <Button
                        color='outline-dark'
                        onClick={() => onCancel()}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </Button>
                    <Button
                        color='primary'
                        onClick={() => {
                            handleSubmit(onSubmit)();
                        }}
                        disabled={isSubmitting || !isValid}
                    >
                        {isSubmitting ? `Saving...` : `Submit`}
                    </Button>
                </Col>
            </Row>
        </Form>
    );
};
